// Sarathi · SPA entry.
// Registers chassis web components, applies font scale, mounts router into #sarathi-route.

import { registerElderComponents, setFontScale, getSelectedLang, detectSystemLang, setSelectedLang } from "./chassis/elder/index.ts";
import { bootstrapRouter, registerRoute } from "./router.ts";
import "./components/pay-cta.ts";
import { renderHome } from "./routes/home.ts";
import { renderMirror } from "./routes/mirror.ts";
import { renderPinned } from "./routes/pinned.ts";
import { renderOnboard } from "./routes/onboard.ts";
import { renderSettings } from "./routes/settings.ts";

const FONT_SCALE_KEY = "empire.elder.font.scale";

function bootstrap(): void {
  // Apply persisted font scale
  if (typeof localStorage !== "undefined") {
    const v = parseFloat(localStorage.getItem(FONT_SCALE_KEY) ?? "1.0");
    const allowed = [1.0, 1.25, 1.5, 2.0];
    const closest = allowed.find((s) => Math.abs(s - v) < 0.001) ?? 1.0;
    setFontScale(closest as 1.0 | 1.25 | 1.5 | 2.0);
  }
  // Make sure a language is set
  if (typeof localStorage !== "undefined" && !localStorage.getItem("empire.elder.lang")) {
    setSelectedLang(detectSystemLang());
  }
  // Register chassis web components (idempotent)
  registerElderComponents();

  // Mount overlay container
  let host = document.getElementById("sarathi-route");
  if (!host) {
    host = document.createElement("div");
    host.id = "sarathi-route";
    host.setAttribute("role", "region");
    host.setAttribute("aria-label", "Sarathi application");
    host.hidden = true;
    document.body.appendChild(host);
  }

  // Register routes
  registerRoute("home", renderHome);
  registerRoute("mirror", renderMirror);
  registerRoute("pinned", renderPinned);
  registerRoute("onboard", renderOnboard);
  registerRoute("settings", renderSettings);
  // /sos resolves through home as a placeholder until F35-class fork ships
  registerRoute("sos", (h) => {
    const card = document.createElement("div");
    card.className = "sarathi-route-shell";
    card.innerHTML = `
      <h1 style="color:var(--sarathi-danger)">SOS</h1>
      <p style="font-size:20px;margin-top:14px">Emergency SOS triggers in v1.1. For now, please dial 112 or 108 directly.</p>
      <div class="sarathi-row" style="margin-top:24px">
        <a href="tel:112" style="display:inline-block;min-height:96px;padding:24px;background:var(--sarathi-danger);color:#fff;border:3px solid #6e0a04;border-radius:14px;font-size:24px;font-weight:700;text-decoration:none">Call 112</a>
        <a href="tel:108" style="display:inline-block;min-height:96px;padding:24px;background:var(--sarathi-danger);color:#fff;border:3px solid #6e0a04;border-radius:14px;font-size:24px;font-weight:700;text-decoration:none">Call 108 ambulance</a>
      </div>
    `;
    h.appendChild(card);
    return () => card.remove();
  });

  bootstrapRouter(host);
  // Wire landing-page Sarathi-shell triggers to /home
  injectLandingTriggers();

  console.log(`[sarathi] booted. lang=${getSelectedLang()}`);
}

function injectLandingTriggers(): void {
  // Add a floating "Open Sarathi" button on the landing page (above the fold)
  if (document.querySelector("#sarathi-open-app")) return;
  const btn = document.createElement("button");
  btn.id = "sarathi-open-app";
  btn.type = "button";
  btn.style.cssText = "position:fixed;bottom:24px;right:24px;z-index:1500;min-width:88px;min-height:88px;padding:18px 28px;background:#c5460e;color:#fff;border:3px solid #9a3306;border-radius:60px;font-size:20px;font-weight:700;cursor:pointer;box-shadow:0 14px 32px rgba(197,70,14,.32);font-family:'Mukta',serif";
  btn.textContent = "सारथी खोलें · Open Sarathi";
  btn.setAttribute("aria-label", "Open Sarathi application");
  btn.addEventListener("click", () => {
    window.location.hash = "#/home";
  });
  document.body.appendChild(btn);

  // Register service worker (best effort)
  if ("serviceWorker" in navigator) {
    void navigator.serviceWorker.register("/sw.js").catch(() => undefined);
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", bootstrap, { once: true });
} else {
  bootstrap();
}
